/**
 * Dashboard Selectors
 * Memoized selectors for efficient dashboard data access and computation
 */

import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/store";
import { DashboardSession, DashboardStats } from "@/types/dashboard.types";
import { logger } from "@/utils/logger"
/**
 * Base selectors (non-memoized)
 */
export const selectDashboardState = (state: RootState) => state.dashboard;

export const selectDashboardSessions = (state: RootState) =>
  state.dashboard.sessions;

export const selectDashboardLoading = (state: RootState) =>
  state.dashboard.loading;

export const selectDashboardError = (state: RootState) => state.dashboard.error;

export const selectDashboardLastFetched = (state: RootState) =>
  state.dashboard.lastFetched;

/**
 * Extract score from session
 * Priority: interviewPracticeFeedback.blocks[0].value (Overall Score)
 * Returns null if no score available
 *
 * Note: Score already comes in 0-10 scale from API, just round to 1 decimal
 */
const extractScore = (session: DashboardSession): number | null => {
  try {
    // Check if realtime_transcript exists and has feedback
    if (
      session.realtime_transcript?.result?.interviewPracticeFeedback?.blocks
        ?.length > 0
    ) {
      const overallScoreBlock =
        session.realtime_transcript.result.interviewPracticeFeedback.blocks.find(
          (block) => block.type === "gauge" && block.title === "Overall Score"
        );

      if (overallScoreBlock && typeof overallScoreBlock.value === "number") {
        // Score already comes in 0-10 scale, round to 1 decimal place
        // Example: 7.67 → 7.7
        return Math.round(overallScoreBlock.value * 10) / 10;
      }
    }

    return null;
  } catch (error) {
    logger.warn("[extractScore] Error extracting score:", error);
    return null;
  }
};

/**
 * Memoized selector: Get sessions sorted by date (most recent first)
 */
export const selectRecentSessions = createSelector(
  [selectDashboardSessions],
  (sessions): DashboardSession[] => {
    return [...sessions].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA; // Most recent first
    });
  }
);

/**
 * Memoized selector: Calculate dashboard statistics
 *
 * Calculates:
 * - Total sessions count (only sessions with realtime_transcript)
 * - Average score (only from sessions with valid scores)
 * - Last practice date
 * - Number of sessions with scores
 */
export const selectDashboardStats = createSelector(
  [selectDashboardSessions],
  (sessions): DashboardStats => {
    // Only count sessions that have realtime_transcript (completed simulations)
    const completedSessions = sessions.filter(
      (s) => s.realtime_transcript !== null
    );
    const totalSessions = completedSessions.length;

    // Extract scores from all sessions (already in 0-10 scale)
    const scores = sessions
      .map(extractScore)
      .filter((score): score is number => score !== null);

    const sessionsWithScores = scores.length;

    // Calculate average score
    // Scores are in 0-10 scale, convert to percentage (0-100) for display
    // Example: avg of [7.7, 8.0, 6.5] = 7.4 → 7.4 * 10 = 74%
    const averageScore =
      sessionsWithScores > 0
        ? Math.round(
            (scores.reduce((sum, score) => sum + score, 0) /
              sessionsWithScores) *
              10
          )
        : null;

    // Get last practice date from completed sessions only
    const lastPracticeDate =
      totalSessions > 0
        ? completedSessions
            .map((s) => s.created_at)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]
        : null;

    return {
      totalSessions,
      averageScore,
      lastPracticeDate,
      sessionsWithScores,
    };
  }
);

/**
 * Memoized selector: Get sessions with their calculated scores
 * Useful for displaying session cards with scores
 */
export const selectSessionsWithScores = createSelector(
  [selectRecentSessions],
  (sessions) => {
    return sessions.map((session) => ({
      ...session,
      calculatedScore: extractScore(session),
    }));
  }
);

/**
 * Memoized selector: Check if cache is valid
 */
export const selectIsCacheValid = createSelector(
  [selectDashboardLastFetched],
  (lastFetched): boolean => {
    if (!lastFetched) return false;
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    return Date.now() - lastFetched < CACHE_DURATION;
  }
);

/**
 * Memoized selector: Get cache age in seconds
 */
export const selectCacheAge = createSelector(
  [selectDashboardLastFetched],
  (lastFetched): number | null => {
    if (!lastFetched) return null;
    return Math.floor((Date.now() - lastFetched) / 1000);
  }
);
